import { useContext, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import SignatureCanvas from 'react-signature-canvas';
import moment from 'moment';

import InputWrapper from "../components/InputWrapper";
import Data from '../../../repository';
import AppContext from "../../../context";

function QuestionRow({ index, question, answer, onChange }) {
    return (
        <div className={"py-3 border-b"}>
            <p className={"mb-2"}>{index}. {question}</p>
            <div className={"flex gap-6"}>
                <label className={"flex items-center gap-2 cursor-pointer font-normal"}>
                    <input type="radio" className={"w-auto"} checked={answer === 'yes'} onChange={() => onChange('yes')} />
                    Yes
                </label>
                <label className={"flex items-center gap-2 cursor-pointer font-normal"}>
                    <input type="radio" className={"w-auto"} checked={answer === 'no'} onChange={() => onChange('no')} />
                    No
                </label>
            </div>
        </div>
    );
}

const HealthScreening = ({ basePath = '' }) => {
    const context = useContext(AppContext);
    const { id } = useParams();
    const location = useLocation();
    const navigate = useNavigate();
    const signatureRef = useRef(null);

    const [loading, setLoading] = useState(false);
    const [questionsLoading, setQuestionsLoading] = useState(true);
    const [questionsError, setQuestionsError] = useState(null);
    const [serverError, setServerError] = useState(null);
    const [signatureError, setSignatureError] = useState(null);
    const [result, setResult] = useState(null);

    const [personal, setPersonal] = useState({
        fullName: context.user ? `${context.user.firstName || ''} ${context.user.lastName || ''}`.trim() : '',
        dateOfBirth: '',
        phoneNumber: context.user?.phoneNumber || '',
        email: context.user?.email || '',
        emergencyContactName: '',
        emergencyContactRelationship: '',
        emergencyContactPhone: ''
    });

    const [section2, setSection2] = useState([]);
    const [section3, setSection3] = useState([]);
    const [protocolAgreed, setProtocolAgreed] = useState(false);
    const [declarationAgreed, setDeclarationAgreed] = useState(false);

    useEffect(() => {
        const loadQuestions = async () => {
            setQuestionsLoading(true);
            setQuestionsError(null);
            try {
                const response = await Data.fetchHealthScreeningQuestions();
                setSection2(response.data.section2.map((q) => ({ _id: q._id, question: q.question, answer: null })));
                setSection3(response.data.section3.map((q) => ({ _id: q._id, question: q.question, answer: null })));
            } catch (error) {
                setQuestionsError('Unable to load the health screening questions. Please try again.');
            }
            setQuestionsLoading(false);
        };
        loadQuestions();
    }, []);

    const updatePersonal = (key, value) => setPersonal((prev) => ({ ...prev, [key]: value }));

    const updateAnswer = (list, setList, index, answer) => {
        setList(list.map((q, i) => i === index ? { ...q, answer } : q));
    };

    const allAnswered = section2.length > 0 && section3.length > 0
        && section2.every((q) => q.answer) && section3.every((q) => q.answer);

    const canSubmit = personal.fullName && personal.dateOfBirth && personal.phoneNumber && personal.email
        && personal.emergencyContactName && personal.emergencyContactRelationship && personal.emergencyContactPhone
        && allAnswered && protocolAgreed && declarationAgreed;

    const returnTo = location.state?.from || `${basePath}/${id}/checkout/book-now`;

    const onSubmit = async (event) => {
        event.preventDefault();
        setServerError(null);
        setSignatureError(null);

        if (!canSubmit) {
            setServerError('Please complete every field, answer every question, and agree to the protocol and declaration before continuing.');
            return;
        }

        if (!signatureRef.current || signatureRef.current.isEmpty()) {
            setSignatureError('Please sign the form to confirm your declaration.');
            return;
        }

        setLoading(true);

        try {
            const signatureImage = signatureRef.current.toDataURL('image/png');
            const signatureDate = moment().format('DD/MM/YYYY');

            const response = await Data.submitHealthScreening({
                personal,
                answers: {
                    section2: section2.map(({ _id, answer }) => ({ questionId: _id, answer })),
                    section3: section3.map(({ _id, answer }) => ({ questionId: _id, answer }))
                },
                protocolAgreed,
                declarationAgreed,
                signatureDate,
                signatureImage
            });

            await context.fetchUser();

            if (response.data?.hasConcerns) {
                setResult('concerns');
                setLoading(false);
            } else {
                navigate(returnTo, { replace: true });
            }
        } catch (error) {
            setServerError(error.response?.data?.message || 'An error occurred. Please try again.');
            setLoading(false);
        }
    };

    if (questionsLoading) {
        return <div className={"p-8"}><p>Loading...</p></div>;
    }

    if (questionsError) {
        return <div className={"p-8"}><p className={"text-red-500"}>{questionsError}</p></div>;
    }

    if (result === 'concerns') {
        return (
            <div className={"p-8 container mx-auto"}>
                <h1>Thanks for completing your health screening</h1>
                <p className={"mt-4"}>
                    One or more of your answers means we need to check in with you before you can make a
                    booking. We've emailed the business with your submission - please contact them directly
                    so they can confirm you're OK to exercise. Once they've reviewed your answers, you'll be
                    able to book as normal.
                </p>
            </div>
        );
    }

    return (
        <div className={"p-8"}>
            <form onSubmit={onSubmit} className={"container mx-auto grid grid-cols-1 gap-4"}>
                <h1>Physical Activity Readiness &amp; Lone Training Screening Questionnaire (PAR-Q+)</h1>
                <p className={"opacity-70"}>
                    This form complies with UK Health and Safety standards, CIMSPA industry guidelines, and the
                    UK Data Protection Act 2018 / UK GDPR for processing special category health data.
                </p>

                <h2>Section 1: Client Personal Details</h2>
                <div className={"grid grid-cols-1 gap-4 lg:grid-cols-2"}>
                    <InputWrapper label={"Full Name"} input={
                        <input type="text" value={personal.fullName} onChange={(e) => updatePersonal('fullName', e.target.value)} />
                    } />
                    <InputWrapper label={"Date of Birth"} input={
                        <input type="date" value={personal.dateOfBirth} onChange={(e) => updatePersonal('dateOfBirth', e.target.value)} />
                    } />
                    <InputWrapper label={"Phone Number"} input={
                        <input type="tel" value={personal.phoneNumber} onChange={(e) => updatePersonal('phoneNumber', e.target.value)} />
                    } />
                    <InputWrapper label={"Email Address"} input={
                        <input type="email" value={personal.email} onChange={(e) => updatePersonal('email', e.target.value)} />
                    } />
                    <InputWrapper label={"Emergency Contact Name"} input={
                        <input type="text" value={personal.emergencyContactName} onChange={(e) => updatePersonal('emergencyContactName', e.target.value)} />
                    } />
                    <InputWrapper label={"Emergency Contact Relationship"} input={
                        <input type="text" value={personal.emergencyContactRelationship} onChange={(e) => updatePersonal('emergencyContactRelationship', e.target.value)} />
                    } />
                    <InputWrapper label={"Emergency Contact Phone"} input={
                        <input type="tel" value={personal.emergencyContactPhone} onChange={(e) => updatePersonal('emergencyContactPhone', e.target.value)} />
                    } />
                </div>

                <h2 className={"mt-4"}>Section 2: Standard Health Readiness (PAR-Q+)</h2>
                <p className={"opacity-70"}>
                    Please answer the health questions below. If you are between the ages of 15 and
                    69, the PAR-Q+ will tell you if you should check with your doctor before you significantly
                    increase your physical activity.
                </p>
                {section2.map((q, i) => (
                    <QuestionRow key={q._id} index={i + 1} question={q.question} answer={q.answer} onChange={(answer) => updateAnswer(section2, setSection2, i, answer)} />
                ))}

                <h2 className={"mt-4"}>Section 3: Solo / Unsupervised Training Risk Assessment</h2>
                <p className={"opacity-70"}>
                    Because your exercise sessions may take place alone and unsupervised, additional safety
                    precautions are required by UK duty-of-care standards to manage risks associated with
                    sudden medical incidents or physical incapacity.
                </p>
                {section3.map((q, i) => (
                    <QuestionRow key={q._id} index={i + 1} question={q.question} answer={q.answer} onChange={(answer) => updateAnswer(section3, setSection3, i, answer)} />
                ))}

                <h2 className={"mt-4"}>Section 4: Solo Training Safety Protocol Agreement</h2>
                <p className={"opacity-70"}>If approved to exercise alone, you agree to adhere strictly to the following safety protocols during every solo session:</p>
                <ul className={"list-disc pl-5 opacity-70 leading-relaxed"}>
                    <li>You will notify your named emergency contact or a responsible adult of your location, start time, and estimated completion time prior to commencing solo training.</li>
                    <li>You will carry a fully charged mobile device or smart wearable equipped with emergency SOS / fall detection capability at all times.</li>
                    <li>If you carry medication (e.g., inhalers, glucose gel, EpiPen), it will be clearly accessible and on your person or in your immediate training area.</li>
                    <li>When training alone with heavy weights, you agree not to perform maximum-load movements without mechanical fail-safes (e.g., using a power rack with safety spotter arms or machine equivalents instead of unspotted barbell bench pressing/squatting).</li>
                    <li>You agree to immediately cease exercising if you feel lightheaded, faint, abnormally short of breath, or experience severe pain.</li>
                </ul>
                <label className={"flex items-center gap-2 cursor-pointer"}>
                    <input type="checkbox" className={"w-auto"} checked={protocolAgreed} onChange={(e) => setProtocolAgreed(e.target.checked)} />
                    I have read and agree to follow the Solo Training Safety Protocol Agreement above.
                </label>

                <h2 className={"mt-4"}>Section 6: UK GDPR Consent &amp; Client Declaration</h2>
                <p className={"opacity-70"}>
                    The personal and health information collected on this form is classified as Special
                    Category Data under the UK Data Protection Act 2018 and UK GDPR. It is processed solely
                    for the purpose of assessing your fitness readiness, ensuring safe physical training
                    conditions, and providing emergency information if required. Your data will be stored
                    securely, kept strictly confidential, and will not be shared with third parties without
                    your explicit consent unless required by law or in a life-threatening emergency.
                </p>
                <p className={"opacity-70"}>
                    I confirm that I have read, understood, and accurately completed this questionnaire; that
                    I acknowledge the inherent risks of physical activity (including delayed assistance in an
                    emergency during solo training); and that I will notify my provider immediately of any
                    changes to my medical status, health conditions, or contact details.
                </p>
                <label className={"flex items-center gap-2 cursor-pointer"}>
                    <input type="checkbox" className={"w-auto"} checked={declarationAgreed} onChange={(e) => setDeclarationAgreed(e.target.checked)} />
                    I declare the above information is true and accurate.
                </label>

                <h2 className={"mt-4"}>Signature</h2>
                <p className={"opacity-70"}>Client Name (Printed): {personal.fullName || '—'} &nbsp; Date: {moment().format('DD/MM/YYYY')}</p>
                <div className={"border rounded-md max-w-[500px]"}>
                    <SignatureCanvas
                        ref={signatureRef}
                        penColor="black"
                        canvasProps={{ width: 500, height: 160, style: { width: '100%', height: '160px', touchAction: 'none' } }}
                    />
                </div>
                <button type="button" className={"bg-backgroundGrey px-2.5 py-1.5 rounded-md border w-fit"} onClick={() => signatureRef.current?.clear()}>
                    Clear Signature
                </button>
                {signatureError ? <p className={"text-red-500 text-sm"}>{signatureError}</p> : null}

                <div>
                    <button type={'submit'} disabled={loading}
                        className={"inline-block bg-primary py-2 px-4 w-full lg:w-auto text-white text-center rounded-md border-0"}>{loading ? 'Submitting...' : 'Submit & Continue'}</button>
                </div>

                {serverError ? <p className={"text-red-500 text-sm"}>{serverError}</p> : null}
            </form>
        </div>
    );
};

export default HealthScreening;
